<template>
    <Head title="Design System" />
    <div class="mx-auto w-full max-w-[1320px] px-6 pb-16 md:px-10">
        <div class="flex flex-col gap-2 pt-6">
            <BasePageTitle title="Design system" subtitle="Internal reference for the components, typography and surfaces available in the product." no-border />
        </div>

        <div class="ds-toolbar">
            <div ref="searchWrapper" class="w-full md:max-w-96">
                <BaseSearchInput v-model="query" aria-label="Filter components" :placeholder="`Filter components — press ${filterShortcutHint} to focus`" />
            </div>
            <p aria-live="polite" aria-atomic="true" class="sr-only">{{ filterStatus }}</p>
            <nav aria-label="Jump to section" class="ds-pills lg:hidden">
                <a v-for="section in visibleSections" :key="section.id" :href="`#${section.id}`" class="ds-pill">{{ section.title }}</a>
            </nav>
        </div>

        <div class="grid grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(0,1fr)_11rem]">
            <div class="flex flex-col gap-12">
                <p v-if="visibleSections.length === 0" class="body text-ink-muted">No components match “{{ query }}”.</p>

                <DesignSystemSection id="actions" title="Actions" description="Things a person clicks to do something.">
                    <DesignSystemEntry
                        name="Button"
                        description="Button themes are not exactly the best (ie. startChat is a temp name given but this should be given a theme name since its so widely used)">
                        <div class="mb-4">
                            <BaseSelect v-model="selectedButtonTheme" :options="buttonThemeOptions" />
                        </div>
                        <div class="flex flex-wrap gap-2 mb-2 items-start">
                            <BaseButton :theme="selectedButtonTheme">{{ selectedButtonTheme }}</BaseButton>
                            <BaseButton :theme="selectedButtonTheme" disabled>{{ selectedButtonTheme }} (disabled)</BaseButton>
                            <a :class="[buttonThemeClass, selectedButtonTheme === 'startChat' ? 'w-fit' : '']">{{ selectedButtonTheme }} (via class)</a>
                            <BaseButton :theme="selectedButtonTheme" size="sm">{{ selectedButtonTheme }} small</BaseButton>
                            <a :class="[buttonThemeClass, 'base-btn-sm', selectedButtonTheme === 'startChat' ? 'w-fit' : '']">{{ selectedButtonTheme }} small (via class)</a>
                        </div>
                        <div class="flex flex-wrap gap-2 mb-2 items-center">
                            <BaseButton :theme="selectedButtonTheme" icon-only aria-label="Add">
                                <i class="bi bi-plus-lg" aria-hidden="true"></i>
                            </BaseButton>
                            <BaseButton :theme="selectedButtonTheme" icon-only size="sm" aria-label="More options">
                                <i class="bi bi-three-dots" aria-hidden="true"></i>
                            </BaseButton>
                            <BaseButton :theme="selectedButtonTheme" icon-only disabled aria-label="Add">
                                <i class="bi bi-plus-lg" aria-hidden="true"></i>
                            </BaseButton>
                            <span class="body-small text-ink-muted">icon-only, icon-only small, icon-only disabled</span>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Dropdown Menu"
                        keywords="action menu context menu"
                        description="an action menu configured as sections with keyboard navigation, automatic separators, and item-specific content slots">
                        <div class="flex gap-2">
                            <BaseDropdownMenu :sections="dropdownMenuSections" @select="handleDropdownMenuSelect">
                                <template #trigger><BaseButton theme="primary">Actions</BaseButton></template>
                            </BaseDropdownMenu>
                            <BaseDropdownMenu :sections="dropdownMenuInstanceSections" label="Switch instance" align="start" @select="handleDropdownMenuSelect">
                                <template #trigger><BaseButton theme="tertiary">With a label</BaseButton></template>
                                <template #item-current-instance="{ item }">
                                    <i class="bi bi-check2 shrink-0 text-base leading-none" aria-hidden="true"></i>
                                    {{ item.label }} <span class="body-small">(current)</span>
                                </template>
                            </BaseDropdownMenu>
                        </div>
                        <span><strong>last action:</strong> {{ lastMenuAction }}</span>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Single-Toggle Group"
                        keywords="segmented control"
                        description="A group of toggle buttons where exactly one option must be selected at all times.">
                        <BaseSingleToggleGroup v-model="activeAlignment" :options="alignments" />
                    </DesignSystemEntry>
                </DesignSystemSection>

                <DesignSystemSection id="forms" title="Forms and inputs" description="Controls that collect a value.">
                    <DesignSystemEntry name="Dropdown" keywords="select" description="a simple key/label scrollable dropdown component, with optional icon">
                        <div class="flex gap-2">
                            <BaseSelect v-model="dropdownSelected" :options="dropdownItems" icon="bi-people-fill" />
                            <BaseSelect placeholder="(Disabled)" disabled />
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Combobox"
                        keywords="autocomplete typeahead"
                        description="a combobox component, which is like a select component with search and autocomplete">
                        <div class="flex flex-col gap-2">
                            <!-- uses default {value: string, label: string} -->
                            <BaseCombobox v-model="selectedDefaultCat" :options="defaultCats"></BaseCombobox>
                            <span><strong>selected:</strong> {{ selectedDefaultCat }}</span>
                            <!-- uses custom {id: string, name: string} and custom display for item-->
                            <BaseCombobox v-model="selectedCat" :options="cats" :display-value="(option) => option.name" :key-value="(option) => option.id">
                                <!-- custom content item, uses slot props -->
                                <template #item="{ option }">
                                    <span class="display-serif">🐱 {{ (option as (typeof cats)[0]).name }}</span>
                                </template>
                            </BaseCombobox>
                            <span><strong>selected:</strong> {{ selectedCat }}</span>
                            <!-- uses custom string[] -->
                            <BaseCombobox v-model="selectedSimpleCat" :options="simpleCats" :display-value="(option) => option" :key-value="(option) => option"></BaseCombobox>
                            <span><strong>selected:</strong> {{ selectedSimpleCat }}</span>
                            <!-- action rows: affordances that produce a value rather than values themselves -->
                            <BaseCombobox
                                v-model="selectedActionCat"
                                v-model:input="catSearch"
                                :options="defaultCats"
                                :actions="catActions"
                                placeholder="Search or add a cat"
                                @action="lastCatAction = $event">
                                <template #action="{ action }">
                                    <i class="bi bi-plus-lg mr-2" aria-hidden="true"></i>
                                    <span>{{ action.label }}</span>
                                </template>
                            </BaseCombobox>
                            <span><strong>selected:</strong> {{ selectedActionCat }}</span>
                            <span><strong>last action:</strong> {{ lastCatAction ?? "(none)" }}</span>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Search Input" description="Bordered search field with a leading magnifier. Full-width by default; compact for header placements.">
                        <div class="flex flex-col gap-3">
                            <BaseSearchInput v-model="designSystemSearch" aria-label="Search example" placeholder="Search for team members" />
                            <BaseSearchInput v-model="designSystemSearch" compact aria-label="Compact search example" placeholder="Search" />
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Base Checkbox" keywords="checkbox tick" description="A simple checkbox component that can be used in forms.">
                        <BaseCheckbox v-model="isCheckboxChecked">Check me out</BaseCheckbox>
                        <BaseCheckbox v-model="isCheckboxCheckedFilled" :is-filled="false">Check me out</BaseCheckbox>
                        <BaseCheckbox v-model="isCheckboxCheckedSquare" shape="square">Check me out</BaseCheckbox>
                        <BaseCheckbox v-model="isCheckboxCheckedSquareFilled" :is-filled="false" shape="square">Check me out</BaseCheckbox>
                        <BaseCheckbox v-model="isCheckboxIndeterminate">Some of us are checked</BaseCheckbox>
                        <BaseCheckbox v-model="isCheckboxSquareIndeterminate" shape="square">Some of us are checked</BaseCheckbox>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Radio Group"
                        description="A single-select radio group built on reka-ui primitives. Supports vertical/horizontal orientation and disabled options/groups.">
                        <div class="flex flex-col gap-6">
                            <BaseRadioGroup v-model="selectedRadioFruit" label="Favorite fruit" :options="radioFruitOptions" />
                            <BaseRadioGroup
                                v-model="selectedRadioAlignment"
                                label="Alignment (horizontal)"
                                orientation="horizontal"
                                :options="alignments.map((a) => ({ value: a.key, label: a.label }))" />
                            <BaseRadioGroup v-model="selectedRadioFruit" label="Disabled group" disabled :options="radioFruitOptions" />
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Switch"
                        keywords="toggle"
                        description="A toggle switch component with three sizes (sm, md, lg). Uses reka-ui Switch primitives under the hood.">
                        <div class="flex flex-col gap-4">
                            <div class="flex items-center gap-3">
                                <BaseSwitch v-model="isSwitchSmChecked" size="sm" />
                                <span class="body-small">Small</span>
                            </div>
                            <div class="flex items-center gap-3">
                                <BaseSwitch v-model="isSwitchMdChecked" size="md" />
                                <span class="body-small">Medium</span>
                            </div>
                            <div class="flex items-center gap-3">
                                <BaseSwitch v-model="isSwitchLgChecked" size="lg" />
                                <span class="body-small">Large (default)</span>
                            </div>
                            <div class="flex items-center gap-3">
                                <BaseSwitch v-model="isSwitchDisabledChecked" disabled />
                                <span class="body-small">Disabled</span>
                            </div>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Slider" keywords="range" description="A slider input component with optional start/end labels and aria support.">
                        <BaseSlider v-model="sliderValue" slider-label="Demo slider" :min="0" :max="5" :step="1" label-start="0" label-end="5" />
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Base Calendar" keywords="date picker" description="Calendar component for single dates.">
                        <BaseCalendar :model-value="selectedCalDate as ZonedDateTime" @update="(date: ZonedDateTime) => (selectedCalDate = date as ZonedDateTime)" />
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Document Uploader" keywords="file upload" description="Demo of document upload, which supports pdf, doc and docx files.">
                        <form @submit.prevent="handleUploadFileSubmit">
                            <input ref="fileInput" aria-label="File Upload" type="file" accept=".pdf,.doc,.docx" class="mb-2" @change="handleFileChange" />
                            <BaseButton theme="primary" type="submit" :disabled="!selectedFile"> Upload Document </BaseButton>
                        </form>
                        <BaseNotification v-if="uploadStatus" :theme="uploadStatus.type as BaseNotificationTheme" :text="uploadStatus.message" />
                    </DesignSystemEntry>
                </DesignSystemSection>

                <DesignSystemSection id="overlays" title="Overlays" description="Surfaces that float above the page.">
                    <DesignSystemEntry name="Dialog" keywords="modal drawer" description="Dialog has its own mobile style that makes it look like a drawer">
                        <!-- can be 2-way binded via v-model:open -->
                        <BaseDialog v-model:open="isDialogShowing" title="Dialog Demo">
                            <template #trigger>
                                <BaseButton theme="primary">Press me to open dialog</BaseButton>
                            </template>
                            <div class="p-4">
                                <p class="body-small">Nested tooltips and popovers and comboboxes within dialog work as expected.</p>
                                <BaseTooltip :as-child="false">
                                    <template #trigger>Hover me!</template>
                                    <template #content>Test tooltip</template>
                                </BaseTooltip>
                                <span class="mx-4"></span>
                                <BasePopover>
                                    <template #trigger>Click me!</template>
                                    <template #content>
                                        <button>1</button>
                                        <button>2</button>
                                        <button>3</button>
                                        <button>4</button>
                                    </template>
                                </BasePopover>
                                <div class="my-4"></div>
                                <BaseCombobox v-model="selectedCat" :options="cats" :display-value="(option) => option.name" :key-value="(option) => option.id" />
                                <div class="my-4"></div>
                                <div v-for="i in 100" :key="i" class="body-small">
                                    Hello I am a very basic dialog with some text content. This text is repeated to demo scrolling in the dialog container.
                                </div>
                            </div>
                        </BaseDialog>
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Popover" description="a generic popover component that can be used to display content that gets portaled up to the body">
                        <div class="flex gap-2">
                            <BasePopover>
                                <template #trigger><BaseButton theme="primary">Trigger</BaseButton></template>
                                <template #content>
                                    <div class="flex flex-col gap-2 w-20">
                                        <button>1</button>
                                        <button>2</button>
                                        <button>3</button>
                                        <button>4</button>
                                    </div>
                                </template>
                            </BasePopover>
                            <BasePopover show-arrow :side-offset="6">
                                <template #trigger><BaseButton theme="neutral">With arrow</BaseButton></template>
                                <template #content>
                                    <p class="body-small p-3">Arrow fill and stroke follow the popover tokens.</p>
                                </template>
                            </BasePopover>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Tooltip" description="Tooltip component is very bare bones, we can style it to whatever shape we want..">
                        <div class="flex flex-wrap gap-4">
                            <BaseTooltip :as-child="false">
                                <template #trigger>Trigger (top)</template>
                                <template #content> This is the tooltip text on the top!</template>
                            </BaseTooltip>
                            <BaseTooltip :as-child="false" side="left">
                                <template #trigger>Trigger (left)</template>
                                <template #content> This is the tooltip text on the left!</template>
                            </BaseTooltip>
                            <BaseTooltip :as-child="false" side="right">
                                <template #trigger>Trigger (right)</template>
                                <template #content> This is the tooltip text on the right!</template>
                            </BaseTooltip>
                            <BaseTooltip :as-child="false" side="bottom">
                                <template #trigger>Trigger (bottom)</template>
                                <template #content> This is the tooltip text on the bottom!</template>
                            </BaseTooltip>
                            <BaseTooltip :as-child="false" theme="dark">
                                <template #trigger>Trigger (dark)</template>
                                <template #content> This is the dark tooltip!</template>
                            </BaseTooltip>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Toast" keywords="snackbar" description="a toast component for displaying brief messages">
                        <div class="flex flex-wrap gap-2">
                            <BaseButton theme="primary" @click="isToastOpen = true">Trigger Toast</BaseButton>
                            <BaseButton theme="primary" @click="isCustomToastOpen = true">Custom content</BaseButton>
                            <BaseButton theme="primary" @click="isErrorToastOpen = true">Error theme</BaseButton>
                            <BaseButton theme="primary" @click="isInfoToastOpen = true">Info theme</BaseButton>
                            <BaseButton theme="primary" @click="isWarningToastOpen = true">Warning theme</BaseButton>
                            <BaseButton theme="primary" @click="isSuccessToastOpen = true">Success theme</BaseButton>
                        </div>
                        <BaseToast
                            v-model:open="isToastOpen"
                            description="This is a toast message. This is a toast message.This is a toast message.This is a toast message.This is a toast message."
                            action-text="I'm an Action">
                        </BaseToast>
                        <BaseToast v-model:open="isCustomToastOpen" description="This is a toast message." action-text="I'm an Action">
                            <template #description><div>🍞 Toast toast toast 🍞</div></template>
                            <template #action>
                                <button class="rounded-xl p-2 gradient-text body-small font-semibold px-4">Close Me</button>
                            </template>
                        </BaseToast>
                        <BaseToast v-model:open="isErrorToastOpen" theme="error" description="This is a error toast message." action-text="I'm an Action"> </BaseToast>
                        <BaseToast v-model:open="isInfoToastOpen" theme="info" description="This is an informational toast message." action-text="I'm an Action"> </BaseToast>
                        <BaseToast v-model:open="isWarningToastOpen" theme="warning" description="This is a warning toast message." action-text="I'm an Action"> </BaseToast>
                        <BaseToast v-model:open="isSuccessToastOpen" theme="success" description="This is a success toast message." action-text="I'm an Action"> </BaseToast>
                    </DesignSystemEntry>
                </DesignSystemSection>

                <DesignSystemSection id="feedback" title="Feedback and status" description="Telling the person what is happening.">
                    <DesignSystemEntry name="Notification" keywords="alert banner">
                        <BaseNotification theme="primary" text="I am a primary notification" />
                        <BaseNotification theme="error" text="I am an error notification (my naming has been fixed, my identity is restored)" />
                        <BaseNotification theme="info" text="I am an actual info notification" />
                        <BaseNotification theme="warning" text="I am a warning notification" />
                        <BaseNotification theme="success" text="I am a success notification" />
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Progress Bar" description="A progress bar component for indicating progress.">
                        <div class="flex flex-col gap-2">
                            empty / partial / full
                            <BaseProgressBar :value="0" show-label />
                            <BaseProgressBar :value="50" show-label />
                            <BaseProgressBar :value="100" show-label />
                            size "xs"
                            <BaseProgressBar :value="20" size="xs" />
                            <BaseProgressBar :value="50" size="xs" show-label />
                            size "sm"
                            <BaseProgressBar :value="20" size="sm" />
                            <BaseProgressBar :value="50" size="sm" show-label />
                            size "md"
                            <BaseProgressBar :value="20" size="md" />
                            <BaseProgressBar :value="100" size="md" show-label />
                            loading
                            <BaseProgressBar :value="0" is-loading size="md" />
                            <BaseProgressBar :value="0" is-loading size="md" show-label />
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Skeleton Loader"
                        keywords="placeholder shimmer"
                        description="Skeleton loader component is very bare bones, we can style it to whatever shape we want, right now only supports pulsing animation.">
                        <SkeletonLoader class="rounded-full w-12 h-12 mb-2" />
                        <SkeletonLoader class="rounded-md w-full h-4 mb-2" />
                        <SkeletonLoader class="rounded-md w-full h-4 mb-2" />
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Loading Spinner" description="A loading spinner component for indicating loading states.">
                        <LoadingSpinner class="h-10 w-10" />
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Orbiting Particles"
                        description="A GSAP-powered loading animation: gradient dots orbit a glowing center on independent paths. Use as an inline indicator or, with `overlay`, as a full-screen loading state.">
                        <OrbitingParticles />
                    </DesignSystemEntry>
                </DesignSystemSection>

                <DesignSystemSection id="content" title="Content" description="Surfaces and labels that carry information.">
                    <DesignSystemEntry name="Card" description="A surface for grouped content. Theme, padding, radius, and interactive states are configurable.">
                        <div class="flex flex-col gap-3">
                            <BaseCard>Default card · padding md</BaseCard>
                            <BaseCard padding="sm" radius="md">Default · padding sm · radius md</BaseCard>
                            <BaseCard theme="muted">Muted theme (no border)</BaseCard>
                            <BaseCard theme="accent">Accent theme (primary tinted)</BaseCard>
                            <BaseCard interactive>Interactive card (hover to see border shift)</BaseCard>
                            <BaseCard theme="cream" radius="lgr" elevated>Cream theme · radius lgr · elevated (in-chat tool card surface)</BaseCard>
                            <BaseCard theme="cream-muted" radius="lgr">Cream muted theme (quiet cream surface, no border)</BaseCard>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Insight layout"
                        keywords="insight card reading"
                        description="Use the default card for standalone insights, or plain columns within a page canvas.">
                        <div class="grid gap-6 md:grid-cols-2">
                            <InsightCardLayout
                                ><h3 class="nadia-subtitle">A standalone insight</h3>
                                <p class="body-small mt-3">The default card keeps its own surface and gradient bar.</p></InsightCardLayout
                            >
                            <InsightCardLayout appearance="plain"
                                ><h3 class="nadia-subtitle">An open reading column</h3>
                                <p class="body-small mt-3">The plain appearance sits directly on the page canvas.</p></InsightCardLayout
                            >
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Chip" keywords="badge tag pill" description="Neutral structural primitive composed into chips with one semantic purpose.">
                        <div class="flex flex-col gap-3">
                            <div class="flex flex-col gap-2">
                                <h4 class="scaffold-body-small-medium text-ink">Sizes</h4>
                                <div class="flex flex-wrap items-center gap-2">
                                    <BaseChip size="2xs">2xs</BaseChip>
                                    <BaseChip size="xs">xs</BaseChip>
                                    <BaseChip>sm</BaseChip>
                                    <BaseChip size="md">md</BaseChip>
                                </div>
                            </div>
                            <div class="flex flex-col gap-2">
                                <h4 class="scaffold-body-small-medium text-ink">Feature chips</h4>
                                <div class="flex flex-wrap gap-2">
                                    <FeatureChip feature="team-reflect">Team Review</FeatureChip>
                                    <FeatureChip feature="self-reflect">Self Reflection</FeatureChip>
                                    <FeatureChip feature="three-sixty">360</FeatureChip>
                                    <FeatureChip feature="journeys">New Journey</FeatureChip>
                                </div>
                            </div>
                            <div class="flex flex-col gap-2">
                                <h4 class="scaffold-body-small-medium text-ink">Source chips</h4>
                                <div class="flex flex-wrap gap-2">
                                    <SourceChip source="nadia">Suggested by Nadia</SourceChip>
                                    <SourceChip source="three-sixty">From 360</SourceChip>
                                    <SourceChip source="self-reflect">From engagement survey</SourceChip>
                                    <SourceChip>From performance conversations</SourceChip>
                                </div>
                            </div>
                            <div class="flex flex-col gap-2">
                                <h4 class="scaffold-body-small-medium text-ink">Status chips</h4>
                                <div class="flex flex-wrap gap-2">
                                    <StatusChip state="active">Active</StatusChip>
                                    <StatusChip state="done">1 of 2 done</StatusChip>
                                    <StatusChip state="pending">Pending</StatusChip>
                                    <StatusChip state="overdue">Overdue</StatusChip>
                                    <StatusChip>No status</StatusChip>
                                </div>
                            </div>
                            <div class="flex flex-col gap-2">
                                <h4 class="scaffold-body-small-medium text-ink">Capability chips</h4>
                                <div class="flex flex-wrap gap-2">
                                    <CapabilityChip>Coach and develop your teammates</CapabilityChip>
                                </div>
                            </div>
                            <div class="flex flex-col gap-2">
                                <h4 class="scaffold-body-small-medium text-ink">Type chips</h4>
                                <div class="flex flex-wrap gap-2">
                                    <BaseChip size="2xs">
                                        <template #leading><i class="bi bi-key" aria-hidden="true"></i></template>
                                        Key meeting
                                    </BaseChip>
                                </div>
                            </div>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Avatar" keywords="initials profile picture" description="Initials, icon, or solid colored dot. Sizes and semantic themes mirror Chip.">
                        <div class="flex flex-col gap-3">
                            <div class="flex flex-wrap items-end gap-2">
                                <BaseAvatar size="xs" initials="A" />
                                <BaseAvatar size="sm" initials="Ab" />
                                <BaseAvatar size="md" initials="AB" theme="primary" />
                                <BaseAvatar size="lg" initials="AB" theme="success" />
                                <BaseAvatar size="xl" initials="AB" theme="warning" />
                            </div>
                            <div class="flex flex-wrap items-end gap-2">
                                <BaseAvatar size="sm" theme="neutral"><i class="bi bi-person" aria-hidden="true"></i></BaseAvatar>
                                <BaseAvatar size="md" theme="primary"><i class="bi bi-people" aria-hidden="true"></i></BaseAvatar>
                                <BaseAvatar size="md" shape="square" theme="primary"><i class="bi bi-stars" aria-hidden="true"></i></BaseAvatar>
                                <BaseAvatar size="sm" theme="danger" />
                                <BaseAvatar size="sm" theme="success" />
                                <BaseAvatar size="sm" theme="warning" />
                            </div>
                            <div class="flex flex-wrap items-end gap-2">
                                <BaseAvatar size="xs" theme="identity" identity-key="Sarah Lin" initials="S" />
                                <BaseAvatar size="xs" theme="identity" identity-key="Sam Stevens" initials="S" />
                                <BaseAvatar size="xs" theme="identity" identity-key="Stacy Carroll" initials="S" />
                                <BaseAvatar size="xs" theme="identity" identity-key="Marley Chen" initials="M" />
                                <BaseAvatar size="xs" theme="identity" identity-key="Charles Okafor" initials="C" />
                                <BaseAvatar size="xs" theme="identity" identity-key="Priya Natarajan" initials="P" />
                            </div>
                        </div>
                    </DesignSystemEntry>
                </DesignSystemSection>

                <DesignSystemSection id="disclosure" title="Navigation and disclosure" description="Showing one slice of content at a time.">
                    <DesignSystemEntry
                        name="Tabs"
                        description="Tab component with ability to customize tab trigger + content for each slot. Supports horizontal (default) and vertical orientation.">
                        <BaseTabs :tab-ids="tabIds">
                            <template v-for="id in tabIds" :key="id" #[`trigger-${id}`]> Tab {{ id }} </template>
                            <template v-for="id in tabIds" :key="id" #[`content-${id}`]> Content {{ id }} </template>
                        </BaseTabs>
                        <div class="mt-4">
                            <p class="body-small mb-2">Vertical</p>
                            <BaseTabs :tab-ids="tabIds" orientation="vertical">
                                <template v-for="id in tabIds" :key="id" #[`trigger-${id}`]> Tab {{ id }} </template>
                                <template v-for="id in tabIds" :key="id" #[`content-${id}`]> Content {{ id }} </template>
                            </BaseTabs>
                        </div>
                        <div class="mt-4">
                            <p class="body-small mb-2">Disabled</p>
                            <BaseTabs :tab-ids="tabIds" disabled>
                                <template v-for="id in tabIds" :key="id" #[`trigger-${id}`]> Tab {{ id }} </template>
                                <template v-for="id in tabIds" :key="id" #[`content-${id}`]> Content {{ id }} </template>
                            </BaseTabs>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Accordion"
                        description="This is the most bare bones Accordion, but its easy enought to extend into a custom version (ie. our nadia notes accordions!)">
                        <BaseAccordion :accordion-ids="accordionIds">
                            <template v-for="id in accordionIds" :key="id" #[`trigger-${id}`]> Trigger {{ id }} </template>
                            <template v-for="id in accordionIds" :key="id" #[`content-${id}`]> Content {{ id }} </template>
                        </BaseAccordion>
                        <p class="body-small mt-4 mb-2">With <code>default-value</code>, expanded on first render:</p>
                        <BaseAccordion :accordion-ids="accordionIds" :default-value="[accordionIds[1]]">
                            <template v-for="id in accordionIds" :key="id" #[`trigger-${id}`]> Trigger {{ id }} </template>
                            <template v-for="id in accordionIds" :key="id" #[`content-${id}`]> Content {{ id }} </template>
                        </BaseAccordion>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Collapsible Section"
                        keywords="expand disclosure"
                        description="This is a wrapper component that can take a custom trigger and display custom content. The expand/collapse is animated and it is fully a11y compliant (screen reader + keyboard).">
                        <BaseCollapsible>
                            <template #trigger> Trigger </template>
                            <template #content> This is just a bunch of random text content I put in to show that it can expand.</template>
                        </BaseCollapsible>

                        <BaseButton theme="primary" class="mt-4" @click="isCollapsibleOpen = !isCollapsibleOpen">
                            Controlled {{ isCollapsibleOpen ? "Collapse" : "Expand" }} Collapsible
                        </BaseButton>

                        <BaseCollapsible v-model:open="isCollapsibleOpen">
                            <template #content> This is just a bunch of random text content I put in to show that it can expand using the controlled component.</template>
                        </BaseCollapsible>

                        <BaseCollapsible stretched-trigger trigger-aria-label="Toggle event details" class="mt-4 rounded-lgr">
                            <template #trigger>
                                <div class="pointer-events-none flex items-center justify-between gap-4 rounded-lgr border border-border p-4">
                                    <span class="body">The whole row toggles this collapsible.</span>
                                    <BaseButton class="relative pointer-events-auto" theme="neutral" size="sm" @click="isStretchedActionActive = !isStretchedActionActive">
                                        Independent action: {{ isStretchedActionActive ? "on" : "off" }}
                                    </BaseButton>
                                </div>
                            </template>
                            <template #content>
                                <div class="p-4">The action remains separate from the stretched trigger.</div>
                            </template>
                        </BaseCollapsible>
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Carousel" keywords="slider slideshow" description="a carousel component for displaying a series of content items">
                        <BaseCarousel :item-ids="['1', '2', '3']">
                            <template v-for="id in [1, 2, 3]" :key="id" #[`item-${id}`]>
                                <div>Item {{ id }}</div>
                            </template>
                        </BaseCarousel>
                        <BaseCarousel :item-ids="['1', '2', '3']" label="Example insights" item-label="insight" animated show-count>
                            <template v-for="id in [1, 2, 3]" :key="id" #[`item-${id}`]>
                                <BaseCard
                                    ><p class="body-small">Insight {{ id }} · horizontal transition with progress</p></BaseCard
                                >
                            </template>
                        </BaseCarousel>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Section navigation"
                        keywords="section nav in-page navigation"
                        description="Compact in-page navigation: horizontal on mobile, a card of section links on desktop. Uses neutral navigation tokens.">
                        <div class="max-w-48">
                            <BaseSectionNav
                                label="Example sections"
                                model-value="you"
                                :items="[
                                    { id: 'you', label: 'About you', icon: 'bi-person', completed: true },
                                    { id: 'calendar', label: 'Calendar', icon: 'bi-calendar-week' },
                                    { id: 'profile', label: 'Collaboration profile', disabled: true, note: 'Complete Perspectives first' },
                                ]" />
                        </div>
                    </DesignSystemEntry>
                </DesignSystemSection>

                <DesignSystemSection id="page-structure" title="Page structure" description="The wrappers every user-facing page is built from.">
                    <DesignSystemEntry
                        name="Layout"
                        wide
                        keywords="page wrapper gutters"
                        description="Standard page wrapper for user-facing pages: page background (bg-valence-page), responsive gutters, and a centered content column that is always a flex column with standard top padding. Configure it with props — width (narrow / default / full), gap (none / sm / md / lg / xl), fill-height, no-top-padding — and reach for content-class only for genuine one-offs. Optional #header slot renders above the column, aligned with the gutters; pass full-bleed-header for edge-to-edge sticky bars (e.g. report action bars).">
                        <div class="flex flex-col gap-6">
                            <BaseLayout class="rounded-lg border border-dashed border-border-strong">
                                <template #header>
                                    <div class="px-6 pt-6 md:px-0 body-small text-ink-muted">#header slot — above the content column, inside the page gutters</div>
                                </template>
                                <div class="rounded-lg border border-border bg-surface-raised p-5 body">Default: centered max-w-225 column, gap-6 between sections</div>
                                <div class="rounded-lg border border-border bg-surface-raised p-5 body">Second section — spacing comes from the default gap</div>
                            </BaseLayout>
                            <BaseLayout width="narrow" gap="sm" class="rounded-lg border border-dashed border-border-strong">
                                <div class="rounded-lg border border-border bg-surface-raised p-5 body">width="narrow" — reading column for reports and results</div>
                                <div class="rounded-lg border border-border bg-surface-raised p-5 body">gap="sm" — tighter rhythm for list-style pages</div>
                            </BaseLayout>
                            <BaseLayout width="full" gap="lg" class="rounded-lg border border-dashed border-border-strong">
                                <div class="rounded-lg border border-border bg-surface-raised p-5 body">width="full" — boards, wide tables, dashboards</div>
                                <div class="rounded-lg border border-border bg-surface-raised p-5 body">gap="lg" — roomier rhythm for section-heavy pages</div>
                            </BaseLayout>
                            <BaseLayout gap="none" content-class="items-center" class="rounded-lg border border-dashed border-border-strong">
                                <div class="rounded-lg border border-border bg-surface-raised p-5 body">
                                    content-class="items-center" — escape hatch for one-off layout. Used twice? Make it a variant.
                                </div>
                            </BaseLayout>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Page Header"
                        wide
                        description="Standard header for user-facing pages: a 20px margin above and below, optional #eyebrow (back link / chip above the title, fixed 12px gap), title, subtitle (full width under the title row with subtitle-below), separator, and an #actions CTA slot. Page top padding belongs to BaseLayout's content column, so the header spaces itself identically whether or not a back link or banner renders above it. Space below the header is the 20px margin plus the page's content gap.">
                        <BasePageHeader
                            title="Page Header"
                            subtitle="A subtitle providing additional context. The separator below spans the full row."
                            class="rounded-lg border border-dashed border-border-strong p-4">
                            <template #eyebrow>
                                <AssessmentEyebrow>Eyebrow slot</AssessmentEyebrow>
                            </template>
                            <template #actions> <BaseButton theme="secondary" size="sm" class="self-start shrink-0">Call to action</BaseButton> </template>
                        </BasePageHeader>
                        <BasePageHeader variant="section" title="Section heading" subtitle="A smaller system heading for a workspace within a page." no-border />
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Page Title"
                        wide
                        description="The title row used inside BasePageHeader — reach for it directly only in embedded contexts (cards, dialogs, wizards). Sets the visible heading (p22 Mackinac Pro, 30px, --color-ink), optional subtitle (16px/22px, --color-page-title-subtitle), and a bottom border separator in --color-border (opt out with no-border). The #actions slot renders a CTA to the right of the title on desktop, stacked below on mobile — its content is a direct flex child (deliberately, so a responsive-hidden CTA leaves no phantom gap), so give it self-start shrink-0 and wrap multiple actions in one element. Pass subtitle-below when the actions leave the text column too narrow for the sentence: the subtitle then gets its own full-width row under the title, wrapped in a div rather than a p so the slot may hold its own inline CTA. Pass subtitle-multiline for admin-authored subcopy, where newlines the author typed should render as breaks. Use Inertia's Head separately in each page for browser tab titles.">
                        <div class="flex flex-col gap-6">
                            <BasePageTitle title="Example Page Title" />
                            <BasePageTitle title="Page Title with Subtitle" subtitle="This is a subtitle that provides additional context about the page." />
                            <BasePageTitle title="Page Title with CTA" subtitle="A button or other call to action passed via the #actions slot appears to the right.">
                                <template #actions>
                                    <BaseButton theme="secondary" size="sm" class="self-start shrink-0">Call to action</BaseButton>
                                </template>
                            </BasePageTitle>
                            <BasePageTitle title="Page Title with Multiline Subtitle" :subtitle="multilineSubtitle" subtitle-multiline />
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Header" keywords="heading" description="Heading component with size and style props.">
                        <div class="flex gap-4 mb-4">
                            <BaseSelect v-model="selectedHeaderSize" :options="headerSizeOptions" />
                            <BaseSelect v-model="selectedHeaderUsage" :options="headerUsageOptions" />
                        </div>
                        <BaseHeader :size="selectedHeaderSize" :usage="selectedHeaderUsage">Heading over one or more lines</BaseHeader>
                    </DesignSystemEntry>
                </DesignSystemSection>

                <DesignSystemSection id="foundations" title="Foundations" description="Type and elevation the components are built on.">
                    <DesignSystemEntry
                        name="Typography"
                        wide
                        keywords="type fonts text styles"
                        description="Typography styles that are used in our design system. Grouped by family: Display (hero), Title (sans), Body (sans), Nadia (Mackinac serif), Scaffold (Space Grotesk), Details (small sans).">
                        <div class="grid grid-cols-1 gap-8 md:grid-cols-2">
                            <section v-for="family in typographyFamilies" :key="family.label">
                                <h4 class="body-small uppercase text-ink-muted">{{ family.label }}</h4>
                                <HorizontalDivider class="mt-0" />
                                <div class="flex flex-col gap-3">
                                    <div v-for="style in family.styles" :key="style">
                                        <div class="scaffold-details-small text-ink-muted">.{{ style }}</div>
                                        <div :class="style">{{ toStyleLabel(style) }}</div>
                                    </div>
                                </div>
                            </section>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Shadows" keywords="elevation box shadow" description="our different box shadow">
                        <div class="flex gap-4">
                            <div class="w-full grid place-content-center h-10 shadow-xs">xs</div>
                            <div class="w-full grid place-content-center h-10 shadow-sm">sm</div>
                            <div class="w-full grid place-content-center h-10 shadow-md">md</div>
                            <div class="w-full grid place-content-center h-10 shadow-lg">lg</div>
                            <div class="w-full grid place-content-center h-10 shadow-xl">xl</div>
                        </div>
                    </DesignSystemEntry>
                </DesignSystemSection>
            </div>

            <nav aria-label="Design system sections" class="ds-rail">
                <p class="scaffold-details-small uppercase text-ink-muted">On this page</p>
                <a
                    v-for="section in visibleSections"
                    :key="section.id"
                    :href="`#${section.id}`"
                    class="ds-rail-link"
                    :class="activeSectionId === section.id && 'is-active'"
                    :aria-current="activeSectionId === section.id ? 'location' : undefined">
                    <span>{{ section.title }}</span>
                    <span class="scaffold-details-small text-ink-muted">{{ section.visible }}</span>
                </a>
            </nav>
        </div>
    </div>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { convertDateTimeToDateValue } from "/js/dateUtils";
import { getCookie } from "/js/utils";
import { Head } from "@inertiajs/vue3";
import type { ZonedDateTime } from "@internationalized/date";
import AssessmentEyebrow from "~vue/components/AssessmentEyebrow.vue";
import BaseAccordion from "~vue/components/BaseAccordion.vue";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseButton, { type ButtonTheme } from "~vue/components/BaseButton.vue";
import BaseCalendar from "~vue/components/BaseCalendar.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseCarousel from "~vue/components/BaseCarousel.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import BaseCollapsible from "~vue/components/BaseCollapsible.vue";
import BaseCombobox, { type ComboboxAction } from "~vue/components/BaseCombobox.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BaseHeader, { type HeaderSize, type HeaderUsage } from "~vue/components/BaseHeader.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification, { type BaseNotificationTheme } from "~vue/components/BaseNotification.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import BasePageTitle from "~vue/components/BasePageTitle.vue";
import BasePopover from "~vue/components/BasePopover.vue";
import BaseProgressBar from "~vue/components/BaseProgressBar.vue";
import BaseSectionNav from "~vue/components/BaseSectionNav.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import BaseSingleToggleGroup from "~vue/components/BaseSingleToggleGroup.vue";
import BaseSwitch from "~vue/components/BaseSwitch.vue";
import BaseTabs from "~vue/components/BaseTabs.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import CapabilityChip from "~vue/components/CapabilityChip.vue";
import FeatureChip from "~vue/components/FeatureChip.vue";
import BaseCheckbox from "~vue/components/form/BaseCheckbox.vue";
import BaseRadioGroup from "~vue/components/form/BaseRadioGroup.vue";
import BaseSearchInput from "~vue/components/form/BaseSearchInput.vue";
import BaseSlider from "~vue/components/form/BaseSlider.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import InsightCardLayout from "~vue/components/InsightCardLayout.vue";
import OrbitingParticles from "~vue/components/OrbitingParticles.vue";
import SkeletonLoader from "~vue/components/SkeletonLoader.vue";
import SourceChip from "~vue/components/SourceChip.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { withRequestId } from "~vue/utils/requestId";
import { DateTime } from "luxon";
import { computed, onBeforeUnmount, onMounted, provide, ref, shallowReactive, useTemplateRef } from "vue";

import { designSystemPageKey, designSystemQueryKey, type RegisteredSection } from "./designSystem/context";
import DesignSystemEntry from "./designSystem/DesignSystemEntry.vue";
import DesignSystemSection from "./designSystem/DesignSystemSection.vue";

// This is an internal only page to showcase some of the design system related assets and components we have

const query = ref("");
provide(designSystemQueryKey, query);

const sections = shallowReactive<RegisteredSection[]>([]);
provide(designSystemPageKey, (section: RegisteredSection) => sections.push(section));

const visibleSections = computed(() =>
    sections.filter((section) => section.visible.value > 0).map((section) => ({ id: section.id, title: section.title, visible: section.visible.value })),
);

const entryTotals = computed(() =>
    sections.reduce((totals, section) => ({ visible: totals.visible + section.visible.value, total: totals.total + section.total.value }), { visible: 0, total: 0 }),
);

const filterStatus = computed(() =>
    query.value.trim()
        ? `${entryTotals.value.visible} of ${entryTotals.value.total} components match “${query.value.trim()}”.`
        : `Showing all ${entryTotals.value.total} components.`,
);

const filterShortcutHint = globalThis.navigator?.platform?.startsWith("Mac") ? "⌘K" : "Ctrl+K";

const activeSectionId = ref("");
let sectionObserver: IntersectionObserver | undefined;

const searchWrapper = useTemplateRef<HTMLElement>("searchWrapper");

// A modifier is required rather than a bare "/": a single-character shortcut with no way to disable,
// remap or scope it fails WCAG 2.1.4 for speech-input and keyboard users.
const focusSearchShortcut = (event: KeyboardEvent) => {
    if (event.key.toLowerCase() !== "k" || !(event.metaKey || event.ctrlKey) || event.altKey) {
        return;
    }
    event.preventDefault();
    searchWrapper.value?.querySelector("input")?.focus();
};

onMounted(() => {
    globalThis.document.addEventListener("keydown", focusSearchShortcut);
    sectionObserver = new IntersectionObserver(
        (entries) => {
            const topMost = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
            if (topMost) {
                activeSectionId.value = topMost.target.id;
            }
        },
        { rootMargin: "-140px 0px -55% 0px" },
    );
    for (const section of sections) {
        const element = globalThis.document.getElementById(section.id);
        if (element) {
            sectionObserver.observe(element);
        }
    }
});

onBeforeUnmount(() => {
    globalThis.document.removeEventListener("keydown", focusSearchShortcut);
    sectionObserver?.disconnect();
});

const typographyFamilies = [
    { label: "Display", styles: ["display-medium", "display-serif", "display-sans-serif", "display-small-serif"] },
    { label: "Title", styles: ["title", "title-section", "title-subsection", "title-body", "title-body-bold", "title-small"] },
    { label: "Body", styles: ["body-light", "body", "body-semibold", "body-bold", "body-small", "body-small-semibold", "body-small-bold", "body-serif"] },
    {
        label: "Nadia (Mackinac serif)",
        styles: ["nadia-chat", "nadia-chat-semibold", "nadia-chat-bold", "nadia-title-subsection", "nadia-subtitle", "nadia-subtitle-small"],
    },
    {
        label: "Scaffold (Space Grotesk)",
        styles: ["scaffold-body", "scaffold-body-bold", "scaffold-body-small", "scaffold-body-small-medium", "scaffold-details-bold", "scaffold-details-small"],
    },
    { label: "Details", styles: ["details", "details-big"] },
];

const toStyleLabel = (style: string) =>
    style
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

const isDialogShowing = ref(false);

const accordionIds = ["1", "2", "3"];

const multilineSubtitle = "The author typed a newline after this sentence.\nSo this renders on its own line.";

const tabIds = ["1", "2", "3"];

const buttonThemeOptions = [
    { key: "primary", label: "Primary" },
    { key: "secondary", label: "Secondary" },
    { key: "tertiary", label: "Tertiary" },
    { key: "neutral", label: "Neutral" },
    { key: "cream", label: "Cream" },
    { key: "danger", label: "Danger" },
    { key: "startChat", label: "Start Chat" },
];
const selectedButtonTheme = ref<ButtonTheme>("primary");
const designSystemSearch = ref("");
const buttonThemeClass = computed(() => {
    const classMap: Record<ButtonTheme, string> = {
        primary: "base-btn-primary",
        secondary: "base-btn-secondary",
        tertiary: "base-btn-tertiary",
        neutral: "base-btn-neutral",
        cream: "base-btn-cream",
        danger: "base-btn-danger",
        startChat: "btn-start-chat",
    };
    return classMap[selectedButtonTheme.value];
});

// Document Uploader
const selectedFile = ref<{ file: File } | null>(null);
const uploadStatus = ref<{ type: string; message: string } | null>(null);

const handleFileChange = async (event: Event) => {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) {
        return;
    }

    selectedFile.value = { file };
};

const fileInput = ref<HTMLInputElement | null>(null);

const uploadFile = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch("/api/chat/document/upload", {
        method: "POST",
        body: formData,
        headers: withRequestId({
            "X-CSRFToken": getCookie("csrftoken") ?? "",
        }),
    });
    return response.json();
};

const handleUploadFileSubmit = async () => {
    if (!selectedFile.value) {
        return;
    }
    try {
        const data = await uploadFile(selectedFile.value.file);
        uploadStatus.value = {
            type: "info",
            message: `File uploaded successfully! File id: ${data.id} (this id should be used to link the file to the chat message)`,
        };
        selectedFile.value = null;
        if (fileInput.value) {
            fileInput.value.value = "";
        }
    } catch (err) {
        uploadStatus.value = {
            type: "secondary",
            message: err instanceof Error ? err.message : String(err),
        };
    }
};

const headerSizeOptions: { key: HeaderSize; label: string }[] = [
    { key: "xxl", label: "XXL (H900)" },
    { key: "xl", label: "XL (H800)" },
    { key: "l", label: "L (H700)" },
    { key: "m", label: "M (H600)" },
    { key: "s", label: "S (H500)" },
    { key: "xs", label: "XS (H400)" },
    { key: "xxs", label: "XXS (H200)" },
];
const headerUsageOptions: { key: HeaderUsage; label: string }[] = [
    { key: "nadia", label: "Nadia" },
    { key: "system", label: "System" },
    { key: "internal-thinking", label: "Internal Thinking" },
];
const selectedHeaderSize = ref<HeaderSize>("m");
const selectedHeaderUsage = ref<HeaderUsage>("system");

const dropdownItems = [
    { key: "option1", label: "Option 1" },
    { key: "option2", label: "Option 2" },
    { key: "option3", label: "Option 3" },
    { key: "option4", label: "Option 4" },
    { key: "option5", label: "Option 5" },
    { key: "option6", label: "Option 6" },
    { key: "option7", label: "Option 7" },
    { key: "option8", label: "Option 8" },
    { key: "option9", label: "Option 9" },
    { key: "option10", label: "Option 10" },
];
const dropdownSelected = ref(dropdownItems[0]!.key);
const lastMenuAction = ref("none");
const dropdownMenuSections: DropdownMenuSection[] = [
    {
        id: "actions",
        items: [
            { id: "edit", type: "button", label: "Edit details", icon: "bi bi-pencil" },
            { id: "merge", type: "button", label: "Merge with another person", icon: "bi bi-front" },
            { id: "link", type: "anchor", href: "/design-system/", label: "Open as a link", icon: "bi bi-box-arrow-up-right" },
            { id: "unavailable", type: "button", label: "Unavailable action", icon: "bi bi-slash-circle", disabled: true },
        ],
    },
    {
        id: "danger",
        items: [{ id: "remove", type: "button", label: "Remove", icon: "bi bi-trash", theme: "danger" }],
    },
];
const dropdownMenuInstanceSections: DropdownMenuSection[] = [
    {
        id: "instances",
        items: [
            { id: "valence-us", type: "button", label: "Valence US", icon: "bi bi-globe" },
            { id: "current-instance", type: "button", label: "Valence EU", disabled: true, ariaCurrent: true },
        ],
    },
];

const handleDropdownMenuSelect = (item: DropdownMenuItem) => {
    lastMenuAction.value = item.id === "merge" ? "Merge" : item.label;
};

const cats = [
    { id: 1, name: "Leona" },
    { id: 2, name: "Munji" },
    { id: 3, name: "Maru" },
    { id: 4, name: "Nori" },
    { id: 5, name: "Mochi" },
];
const selectedCat = ref(cats[0]);

const simpleCats = cats.map((cat) => cat.name);
const selectedSimpleCat = ref(simpleCats[0]);

const defaultCats = cats.map((cat) => ({ value: cat.id, label: cat.name }));
const selectedDefaultCat = ref(defaultCats[0]);

const selectedActionCat = ref(defaultCats[0]);
const catSearch = ref("");
const lastCatAction = ref<string | null>(null);
const catActions = computed<ComboboxAction[]>(() => {
    const name = catSearch.value.trim();
    const create: ComboboxAction[] =
        name && !defaultCats.some((cat) => cat.label.toLowerCase() === name.toLowerCase()) ? [{ id: "create", label: `Add “${name}” as a new cat` }] : [];
    return [
        ...create,
        {
            id: "adopt",
            label: lastCatAction.value === "adopt" ? "Copied" : "None of these? Copy adoption link",
            closeOnSelect: false,
        },
    ];
});

const isToastOpen = ref(false);
const isCustomToastOpen = ref(false);
const isErrorToastOpen = ref(false);
const isInfoToastOpen = ref(false);
const isWarningToastOpen = ref(false);
const isSuccessToastOpen = ref(false);

const alignments = [
    { key: "left", label: "Left" },
    { key: "center", label: "Center" },
    { key: "right", label: "Right" },
];
const activeAlignment = ref("center");

const radioFruitOptions = [
    { value: "apple", label: "Apple" },
    { value: "banana", label: "Banana" },
    { value: "cherry", label: "Cherry", disabled: true },
];
const selectedRadioFruit = ref("apple");
const selectedRadioAlignment = ref("center");

const isCheckboxChecked = ref(true);

const isSwitchSmChecked = ref(false);
const isSwitchMdChecked = ref(true);
const isSwitchLgChecked = ref(true);
const isSwitchDisabledChecked = ref(false);

const selectedCalDate = ref<ZonedDateTime>(convertDateTimeToDateValue(DateTime.now()));

const sliderValue = ref([3]);

const isCheckboxCheckedFilled = ref(true);
const isCheckboxCheckedSquare = ref(true);
const isCheckboxCheckedSquareFilled = ref(true);
const isCheckboxIndeterminate = ref<boolean | "indeterminate">("indeterminate");
const isCheckboxSquareIndeterminate = ref<boolean | "indeterminate">("indeterminate");
const isCollapsibleOpen = ref(false);
const isStretchedActionActive = ref(false);
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .ds-toolbar {
        @apply sticky top-0 z-20 -mx-6 mb-8 flex flex-col gap-3 border-b border-border bg-surface px-6 py-3 md:-mx-10 md:px-10;
    }

    .ds-pills {
        @apply -mx-6 flex gap-2 overflow-x-auto px-6 md:-mx-10 md:px-10;
        scrollbar-width: none;
    }

    .ds-pills::-webkit-scrollbar {
        display: none;
    }

    .ds-pill {
        @apply shrink-0 rounded-full border border-border bg-surface-raised px-3 py-1 scaffold-details-small text-ink no-underline;
    }

    .ds-rail {
        @apply hidden flex-col gap-1 lg:sticky lg:top-32 lg:flex;
    }

    .ds-rail-link {
        @apply flex items-center justify-between gap-2 rounded-md border-l-2 border-transparent px-2 py-1 body-small text-ink-muted no-underline hover:bg-surface-hover;
    }

    .ds-rail-link.is-active {
        @apply border-accent text-ink;
    }
}
</style>
